import { useRef } from 'react';
import { motion } from 'framer-motion';
import { Sparkles, ArrowDown } from 'lucide-react';
import { useTheme } from '../Theme/ThemeProvider';
import { Link } from 'react-router-dom';
import Silk from '../ui/Silk';
import { useMousePosition } from '../../hooks/useMousePosition';
import { fadeInUp, fadeInScale, staggerContainer, get3DTiltStyle } from '../../utils/animations';

const Hero3D = () => {
  const { theme } = useTheme();
  const prefersDark = typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches;
  const isDark = theme === 'dark' || (theme === 'system' && prefersDark) || (typeof document !== 'undefined' && document.documentElement.classList.contains('dark'));
  const silkColor = isDark ? '#1a1a2e' : '#EEF2FF';
  const mousePosition = useMousePosition();
  const cardRef = useRef<HTMLDivElement>(null);

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Silk Background */}
      <div className="absolute inset-0 z-0">
        <Silk speed={5} scale={1} color={silkColor} noiseIntensity={1.5} rotation={0} />
      </div>

      {/* Hero Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 mt-20">
        <motion.div
          variants={staggerContainer}
          initial="initial"
          animate="animate"
          className="text-center"
        >
          {/* Badge */}
          <motion.div
            variants={fadeInScale}
            className="inline-flex items-center gap-2 px-4 py-2 glass-morphism rounded-full mb-8"
          >
            <Sparkles size={16} className="text-neon-purple" />
            <span className="text-sm font-medium text-text-secondary">Welcome to the Future</span>
          </motion.div>

          {/* Main Heading */}
          <motion.h1
            variants={fadeInUp}
            className="text-display font-bold mb-6 leading-tight"
          >
            Building Tomorrow's
            <br />
            <span style={{ color: '#FCD34D' }}>Technology Today</span>
          </motion.h1>

          {/* Description */}
          <motion.p
            variants={fadeInUp}
            className="text-body-lg text-text-secondary mb-12 max-w-3xl mx-auto"
          >
            Pioneering innovations in AI, quantum computing, space technology, and beyond.
            Transforming visionary ideas into groundbreaking realities.
          </motion.p>

          {/* CTA Cards with 3D Tilt Effect */}
          <motion.div
            variants={staggerContainer}
            className="flex flex-col sm:flex-row gap-6 justify-center items-center"
          >
            <motion.div
              ref={cardRef}
              variants={fadeInScale}
              whileHover={{ scale: 1.05 }}
              style={get3DTiltStyle(mousePosition.x, mousePosition.y, cardRef.current)}
              className="group cursor-pointer"
            >
            </motion.div>

            <motion.div
              variants={fadeInScale}
              whileHover={{ scale: 1.05 }}
              className="group cursor-pointer"
            >
              <a
                href="#about"
                className="block px-10 py-5 glass-morphism text-text-primary font-bold rounded-2xl hover:border-neon-purple transition-all duration-300"
              >
                <span className="text-lg">Learn More</span>
                
              </a>
            </motion.div>
          </motion.div>

          {/* Scroll Indicator */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.5, duration: 0.8 }}
            className="mt-20"
          >
            <motion.div
              animate={{ y: [0, 10, 0] }}
              transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
              className="inline-flex flex-col items-center gap-2 text-text-muted"
            >
              <span className="text-sm">Scroll to explore</span>
              <ArrowDown size={20} className="animate-pulse" />
            </motion.div>
          </motion.div>
        </motion.div>
      </div>

      {/* Gradient Overlays */}
      <div className="absolute top-0 left-0 w-full h-full pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-neon-purple/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-neon-blue/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
      </div>
    </section>
  );
};

export default Hero3D;

